import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Loader from "../../components/elements/Loader";
import SnackbarAlert from "../../components/elements/SnackbarAlert";
import { useFormData } from "../../helper/FormDataProvider";
import useLandingAuthApi from "../../services/api/useLandingAuthApi";
import {
  ClearanceStatus,
  TRIP_STATUS,
  AIRPORT_TYPE,
  TRIP_DETAILS_ACTION_MESSAGES,
} from "../../utils/constants";
import {
  customDateFormat,
  getFormattedLocalTime,
  getFormattedLocalTimeWithMinusHour,
} from "../../utils/dateUtils";
import ROUTE_PATHS from "../../utils/routePaths";
import { airportCodeOnApprove } from "../../utils/Utils";
import "./TripDetails.css";
import TripDetailsHeader from "./TripDetailsHeader";
import ActionButtons from "./views/ActionButtons";
import AfterHoursCard from "./views/AfterHoursCard";
import AircraftDetails from "./views/AircraftDetails";
import AirportLocationInformation from "./views/AirportLocationInformation";
import PassengerInformation from "./views/PassengersInfo";
import PaymentInformation from "./views/PaymentInformation";
import SpecialOnboardScreeningCard from "./views/SpecialOnboardScreeningCard";
import TripID from "./views/TripID";
import ApprovalStatus from "../HistoricTrips/views/ApprovedStatus";
import PersonalDetails from "./views/PersonalDetails";

const TripDetails = () => {
  const { getTripById } = useLandingAuthApi();
  const [resolvedMtow, setResolvedMtow] = useState();

  const { tripId: routeTripId } = useParams();
  const { userData } = useFormData();
  const location = useLocation();
  const navigate = useNavigate();
  const fromScreen = location?.state?.fromScreen;
  const stateTripId =
    location?.state?.tripId ||
    location?.state?.itemId ||
    location?.state?.tripData?.id ||
    location?.state?.trip?.id;
  const isValidTripId = (id) =>
    Boolean(id) && id !== ":tripId" && id !== "undefined" && id !== "null";
  const tripId = isValidTripId(routeTripId)
    ? routeTripId
    : isValidTripId(stateTripId)
      ? stateTripId
      : null;

  const {
    data: tripDetails,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ["tripDetails", tripId],
    queryFn: () => getTripById(tripId),
    enabled: Boolean(tripId),
    refetchOnMount: "always",
    retry: false,
  });

  const tripData = tripDetails?.data;
  const isExempted = tripData?.isExempted;
  const currentPage = location?.state?.currentPage;
  const handleEscortClick = (trips) => {
    localStorage.setItem("currentPage", currentPage);
    localStorage.setItem("fromScreen", "tripDetails");
    navigate(ROUTE_PATHS.ESCORT_DETAILS(trips?.id), {
      state: {
        currentPage,
        fromScreen: "tripDetails",
      },
    });
  };
  const handleAfterHoursClick = (trips) => {
    localStorage.setItem("currentPage", currentPage);
    localStorage.setItem("fromScreen", "tripDetails");
    navigate(ROUTE_PATHS.AFTER_HOURS_DETAILS(trips?.id), {
      state: {
        currentPage,
        fromScreen: "tripDetails",
      },
    });
  };

  const checkDepartureOvertime = () => {
    if (tripData?.requiresDepartureOvertime) {
      return {
        departureAirportOpeningTime: getFormattedLocalTimeWithMinusHour(
          tripData.departureTime
        ),
        scheduledTime: getFormattedLocalTime(tripData.departureTime),
      };
    }
  };
  const checkArrivalOvertime = () => {
    if (tripData?.requiresArrivalOvertime) {
      return {
        requiresArrivalClosingOvertime: getFormattedLocalTimeWithMinusHour(
          tripData?.arrivalTime
        ),
        scheduledTime: getFormattedLocalTime(tripData?.arrivalTime),
      };
    }
  };
  const timingDepartureInfo = tripData?.requiresDepartureOvertime
    ? checkDepartureOvertime()
    : null;
  const timingArrivalInfo = tripData?.requiresArrivalOvertime
    ? checkArrivalOvertime()
    : null;

  const isValidStatus = (status) =>
    status !== ClearanceStatus.NOT_REQUIRED &&
    status !== ClearanceStatus.CLEARANCE_REJECTED;

  const overtimeStatus = isValidStatus(tripData?.overtimeArrivalStatus)
    ? tripData?.overtimeArrivalStatus
    : isValidStatus(tripData?.overtimeDepartureStatus)
      ? tripData?.overtimeDepartureStatus
      : null;

  const escortStatus = isValidStatus(tripData?.escortDepartureStatus)
    ? tripData?.escortDepartureStatus
    : isValidStatus(tripData?.escortArrivalStatus)
      ? tripData?.escortArrivalStatus
      : null;

  const hasBothTerminals =
    tripData?.arrivalAirportTerminal && tripData?.departureAirportTerminal;
  const createdBy = tripData?.aircraft?.ownerProfile?.firstName &&
    tripData?.aircraft?.ownerProfile?.lastName
    ? `${tripData?.aircraft?.ownerProfile?.firstName} ${tripData?.aircraft?.ownerProfile?.lastName}` : "";
  const contactNumber =
    tripData?.contactNumber || tripData?.aircraft?.ownerProfile?.phone || "---";
  const contactEmail =
    tripData?.aircraft?.ownerProfile?.email || tripData?.creatorEmail || "---";

  const shouldShowActionButtons = () => {
    // If both terminals exist, hide action buttons
    if (hasBothTerminals) {
      return false;
    }

    // If departure airport is INTL and arrival terminal exists, hide action buttons
    if (tripData?.departureAirport?.airportType === AIRPORT_TYPE.INTL && tripData?.arrivalAirportTerminal != null) {
      return false;
    }

    // If departure terminal exists and arrival airport is INTL, hide action buttons
    if (tripData?.departureAirportTerminal != null && tripData?.arrivalAirport?.airportType === AIRPORT_TYPE.INTL) {
      return false;
    }

    // Otherwise, show action buttons
    return true;
  };
  const hasPassengersRequiringEscort = tripData?.passengers?.some(
    (passenger) => passenger.requiresEscortService
  );
  const isDepartureDateArrived = () => {
    if (!tripData?.departureTime) return false;
    const departureDate = new Date(tripData.departureTime);
    const currentDate = new Date();
    const departureDateOnly = new Date(
      departureDate.getFullYear(),
      departureDate.getMonth(),
      departureDate.getDate()
    );
    const currentDateOnly = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate()
    );

    return currentDateOnly >= departureDateOnly;
  };
  if (error) {
    return (
      <SnackbarAlert type="error" data-testid="error-message">Error loading trip details.</SnackbarAlert>
    );
  }
  if (!tripId) {
    return (
      <SnackbarAlert type="error" data-testid="error-message">
        Unable to open trip details. Missing trip ID.
      </SnackbarAlert>
    );
  }

  return (
    <div className="trip-details-container">
      <TripDetailsHeader currentPage={currentPage} fromScreen={fromScreen} />
      {isLoading && <Loader />}
      {!isLoading && (
        <div className="trip-details-row">
          <div className="trip-details-left-column">
            <TripID id={tripData?.trackingId} />
            <PersonalDetails
              createdBy={createdBy}
              contactNumber={contactNumber}
              email={contactEmail}
            />
            <AircraftDetails aircraft={tripData?.aircraft} onMtowResolved={setResolvedMtow} />
            <AirportLocationInformation tripData={tripData} showStatus={true} />
            {(tripData?.requiresDepartureOvertime ||
              tripData?.requiresArrivalOvertime) &&
              (timingArrivalInfo || timingDepartureInfo) && (
                <AfterHoursCard
                  departureTimings={
                    tripData?.requiresDepartureOvertime
                      ? `${timingDepartureInfo.departureAirportOpeningTime} to ${timingDepartureInfo.scheduledTime}`
                      : null
                  }
                  arrivalTimings={
                    tripData?.requiresArrivalOvertime
                      ? `${timingArrivalInfo.requiresArrivalClosingOvertime} to ${timingArrivalInfo.scheduledTime}`
                      : null
                  }
                  departureAirport={airportCodeOnApprove(
                    tripData?.departureAirport.iataCode,
                    tripData?.departureAirport.icaoCode
                  )}
                  arrivalAirport={airportCodeOnApprove(
                    tripData?.arrivalAirport.iataCode,
                    tripData?.arrivalAirport.icaoCode
                  )}
                  onClick={() => handleAfterHoursClick(tripData)}
                />
              )}
            {(hasPassengersRequiringEscort ||
              (tripData?.escortVehicles &&
                tripData?.escortVehicles.length > 0)) && (
                <SpecialOnboardScreeningCard
                  onClick={() => handleEscortClick(tripData)}
                />
              )}
          </div>
          <div className="trip-details-left-column">
            {tripData?.passengers && tripData?.passengers?.length > 0 && (
              <PassengerInformation passengers={tripData?.passengers} />
            )}

            {!isExempted && (
              <PaymentInformation
                tripId={tripId}
                overtimeStatus={overtimeStatus}
                escortStatus={escortStatus}
                hasBothTerminals={hasBothTerminals}
                tripStatus={tripData?.status}
                arrivalTime={tripData?.arrivalTime}
                departureTimeFromArrivalAirport={tripData?.departureTimeFromArrivalAirport}
                mtow={resolvedMtow}
              />
            )}
            {(tripData?.status === TRIP_STATUS.COMPLETED) && (
              <ApprovalStatus
                isTripCancelled={tripData?.status === TRIP_STATUS.CANCELLED}
                email={isExempted ? tripData?.exemptedByEmail : tripData?.approvedByEmail}
                airport={airportCodeOnApprove(
                  tripData?.arrivalAirport?.iataCode,
                  tripData?.arrivalAirport?.icaoCode
                )}
                isAutoCompleted={tripData?.isAutoCompleted}
                isExempted={isExempted}
                trackingId={tripData?.trackingId}
                timestamp={customDateFormat(isExempted ? tripData?.exemptedAt : tripData?.approvedAt)}
              />
            )}
          </div>
        </div>
      )}
      {!isLoading &&
        tripData?.status !== TRIP_STATUS.CANCELLED &&
        shouldShowActionButtons() && (
          <>
            {!isDepartureDateArrived() && (
              <div className="action-buttons-message">
                <span className="info-icon">ℹ️</span>
                {TRIP_DETAILS_ACTION_MESSAGES.DEPARTURE_DATE_NOT_ARRIVED}
              </div>
            )}
            <ActionButtons
              arrivalAirport={airportCodeOnApprove(
                tripData?.arrivalAirport?.iataCode,
                tripData?.arrivalAirport?.icaoCode
              )}
              trackingId={tripData?.trackingId}
              tripStatus={tripData?.status}
              userData={userData}
              tripData={tripData}
              canTakeAction={isDepartureDateArrived()}
              isRefreshing={isFetching}
            />
          </>
        )}
    </div>
  );
};

export default TripDetails;
